import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Logo from "../components/Logo";
import { toast } from "sonner";
import {
  ArrowLeft,
  Users,
  Store,
  Clock,
  Mail,
  CheckCircle2,
  XCircle,
  Loader2,
  Bell,
  RefreshCw
} from "lucide-react";

const AdminWaitlist = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [waitlist, setWaitlist] = useState([]);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [error, setError] = useState(null);

  const fetchWaitlist = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/admin/billing-waitlist");
      setWaitlist(res.data.users || []);
      setBillingEnabled(res.data.billing_enabled);
    } catch (err) {
      console.error("Failed to fetch waitlist:", err);
      setError(err.response?.data?.detail || "Failed to load waitlist");
      toast.error("Failed to load waitlist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWaitlist();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Logo showText linkTo="/" />
          <div className="flex items-center gap-4">
            <span className="text-text-muted text-sm hidden md:block">{user?.email}</span>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="icon" className="text-text-muted hover:text-white">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold flex items-center gap-3">
                  <Bell className="w-6 h-6 text-[#D4F34A]" />
                  Billing Waitlist
                </h1>
                <p className="text-text-muted text-sm mt-1">
                  Users waiting for billing to open ({waitlist.length})
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Billing Status Badge */}
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                billingEnabled 
                  ? "bg-green-500/20 text-green-400" 
                  : "bg-yellow-500/20 text-yellow-400"
              }`}>
                {billingEnabled ? "Billing Enabled" : "Billing Pending"}
              </div>
              
              <Button
                onClick={fetchWaitlist}
                disabled={loading}
                variant="outline"
                size="sm"
                className="border-[#262626] text-white hover:bg-white/5"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <Card className="bg-red-500/10 border-red-500/30 mb-6">
              <CardContent className="py-4">
                <p className="text-red-400">{error}</p>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {loading && !error && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && waitlist.length === 0 && (
            <Card className="bg-surface border-[#262626]">
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-[#262626] flex items-center justify-center mx-auto mb-4">
                  <Bell className="w-8 h-8 text-text-muted" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No users on the waitlist</h3>
                <p className="text-text-muted">
                  No users have requested billing notifications yet.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Waitlist Table */}
          {!loading && !error && waitlist.length > 0 && (
            <Card className="bg-surface border-[#262626]">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="waitlist-table">
                    <thead>
                      <tr className="border-b border-[#262626]">
                        <th className="text-left py-4 px-6 text-sm font-medium text-text-muted">Email</th>
                        <th className="text-left py-4 px-4 text-sm font-medium text-text-muted">Signup Date</th>
                        <th className="text-center py-4 px-4 text-sm font-medium text-text-muted">Store Connected</th>
                        <th className="text-left py-4 px-4 text-sm font-medium text-text-muted">Shop Domain</th>
                        <th className="text-left py-4 px-4 text-sm font-medium text-text-muted">Affiliate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waitlist.map((user, idx) => (
                        <tr 
                          key={user.user_id || idx} 
                          className="border-b border-[#262626] last:border-0 hover:bg-white/5 transition-colors"
                          data-testid={`waitlist-row-${idx}`}
                        >
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#262626] flex items-center justify-center">
                                <Mail className="w-4 h-4 text-text-muted" />
                              </div>
                              <div>
                                <p className="text-white font-medium">{user.email}</p>
                                {user.name && (
                                  <p className="text-text-muted text-xs">{user.name}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2 text-text-muted text-sm">
                              <Clock className="w-3.5 h-3.5" />
                              {formatDate(user.created_at)}
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            {user.store_connected ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/10 text-green-400 text-xs font-medium">
                                <CheckCircle2 className="w-3 h-3" />
                                Yes
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#262626] text-text-muted text-xs font-medium">
                                <XCircle className="w-3 h-3" />
                                No
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {user.shop_domain ? (
                              <div className="flex items-center gap-2">
                                <Store className="w-3.5 h-3.5 text-text-muted" />
                                <span className="text-sm text-white">{user.shop_domain}</span>
                              </div>
                            ) : (
                              <span className="text-text-muted text-sm">—</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {user.affiliate_code ? (
                              <div>
                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#D4F34A]/10 text-[#D4F34A] text-xs font-medium">
                                  {user.affiliate_code}
                                </span>
                                {user.affiliate_name && (
                                  <p className="text-text-muted text-xs mt-1">{user.affiliate_name}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-text-muted text-sm">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary Card */}
          {!loading && !error && waitlist.length > 0 && (
            <Card className="bg-surface border-[#262626] mt-6">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-text-muted text-sm">Total Waitlist</p>
                      <p className="text-2xl font-bold text-white">{waitlist.length}</p>
                    </div>
                    <div className="w-px h-10 bg-[#262626]" />
                    <div>
                      <p className="text-text-muted text-sm">With Store Connected</p>
                      <p className="text-2xl font-bold text-green-400">
                        {waitlist.filter(u => u.store_connected).length}
                      </p>
                    </div>
                  </div>
                  
                  <Link to="/admin">
                    <Button variant="outline" className="border-[#262626] text-white hover:bg-white/5">
                      Back to Overview
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminWaitlist;
